import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatFieldErrors } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import './auth.css';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
    } finally {
      setBusy(false);
      setSent(true); // identical outcome either way — no account enumeration
    }
  }

  return (
    <div className="auth-page">
      <Card className="auth-card" title="Reset your password">
        {sent ? (
          <>
            <div className="auth-info">
              If an account exists for <strong>{email}</strong>, a reset link is on its way.
            </div>
            <Link className="auth-inline-link" to="/login">
              Back to sign in
            </Link>
          </>
        ) : (
          <form onSubmit={submit}>
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" loading={busy} className="auth-full-width">
              Send reset link
            </Button>
            <div className="auth-links">
              <span />
              <Link to="/login">Back to sign in</Link>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          userId: params.get('uid'),
          token: params.get('token'),
          newPassword: password,
        }),
      });
      toast('Password updated — sign in with your new password.', 'success');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(formatFieldErrors(err, 'This reset link is invalid or has expired.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <Card className="auth-card" title="Choose a new password">
        {error ? <div className="auth-error">{error}</div> : null}
        <form onSubmit={submit}>
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            hint="At least 10 characters with upper & lower case, a number and a symbol"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
          />
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          <Button type="submit" loading={busy} className="auth-full-width">
            Set password
          </Button>
        </form>
      </Card>
    </div>
  );
}
